import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "About", href: "#about" },
  { label: "How I Help", href: "#services" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Why Fatima", href: "#why" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, delay: 1.6, ease: [0.83, 0, 0.17, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-plum/75 border-b border-gold/15"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between py-5">
        <a
          href="#top"
          data-testid="nav-logo"
          className="group flex items-center gap-3"
        >
          <span className="w-10 h-10 border border-gold/60 flex items-center justify-center font-serif text-gold text-lg tracking-wider group-hover:bg-gold group-hover:text-plum transition-colors duration-500">
            FYA
          </span>
          <span data-testid="nav-brand-text" className="flex flex-col leading-tight">
            <span className="font-serif text-cream text-xs sm:text-sm tracking-[0.22em] sm:tracking-[0.25em]">
              PROPERTY
            </span>
            <span className="font-serif text-cream/70 text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.25em]">
              GROUP
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              href={l.href}
              className="text-cream/80 hover:text-gold text-xs uppercase tracking-[0.28em] link-underline transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-connect"
            href="#connect"
            className="hidden md:inline-flex items-center gap-2 px-6 py-3 bg-gold text-plum text-xs uppercase tracking-[0.28em] font-medium hover:bg-cream transition-colors duration-500"
          >
            Connect
            <span className="w-1 h-1 rounded-full bg-plum" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-cream p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.83, 0, 0.17, 1] }}
            className="lg:hidden overflow-hidden bg-plum/95 backdrop-blur-xl border-t border-gold/15"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <a
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl text-cream hover:text-gold transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#connect"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gold text-plum text-xs uppercase tracking-[0.28em] w-fit"
              >
                Connect
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
